import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MDXEditor,
  BoldItalicUnderlineToggles,
  ConditionalContents,
  ChangeCodeMirrorLanguage,
  CreateLink,
  DiffSourceToggleWrapper,
  InsertCodeBlock,
  InsertThematicBreak,
  HighlightToggle,
  InsertImage,
  InsertTable,
  ListsToggle,
  StrikeThroughSupSubToggles,
  UndoRedo,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  headingsPlugin,
  imagePlugin,
  linkPlugin,
  linkDialogPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  type MDXEditorMethods,
  BlockTypeSelect,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css'
import { toast } from 'sonner';
import { wikiArticleApi, wikiTocApi, imageApi } from '../utils/api';
import { resolveApiUrl } from '../utils/apiBase';

interface WikiTOC {
    id: string;
    parentId: string | null;
    description: string;
    sortOrder: number;
    children?: WikiTOC[];
}

const WikiEditorPage: React.FC = () => {
  const mdxEditorRef = React.useRef<MDXEditorMethods>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [tocItems, setTocItems] = useState<WikiTOC[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [originalMarkdown, setOriginalMarkdown] = useState('');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [tocId, setTocId] = useState('');
  const [isPublished, setIsPublished] = useState(true);

  // ── Load data ──

  const fetchArticle = useCallback(async () => {
    if (!id) return '';
    const result = await wikiArticleApi.getById(id);
    if (!result.error && result.data) {
      const article = result.data as any;
      setTitle(article.title);
      setTags(article.tags || '');
      setTocId(article.tocId ?? article.TOCId);
      setIsPublished(article.isPublished);
      return article.content;
    }
    return '';
  }, [id]);

  const fetchToc = useCallback(async () => {
    const result = await wikiTocApi.getAll();
    if (!result.error) {
      setTocItems((result.data || []).map((t: any) => ({ ...t, parentId: t.parentId ?? t.ParentId })));
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const content = await fetchArticle();
      await fetchToc();
      setMarkdown(content);
      setOriginalMarkdown(content);
      setLoading(false);
    };
    load();
  }, [fetchArticle, fetchToc]);

  // ── Save ──

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
        setError('Title is required.');
        return;
    }
    const mdContent = markdown.trim();
    if (!mdContent) {
        setError('Content is required.');
        return;
    }
    if (!tocId) {
        setError('Please select a section.');
        return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    // Authorship is no longer sent: the API records it from the validated
    // token. Sending it was how a caller could author as anyone at all.
    const dto = {
      title: title.trim(),
      content: mdContent,
      tags: tags.trim() || undefined,
      tocId,
      isPublished,
    };

    let result;
    if (isEditing) {
      result = await wikiArticleApi.update(id!, dto);
    } else {
      result = await wikiArticleApi.create(dto);
    }

    if (result.error) {
      setError(result.error);
    } else {
      setSuccess(true);
      setTimeout(() => navigate(`/wiki/article/${(result.data as any).id}`), 1000);
    }
    setSaving(false);
  };

  // ── TOC helpers ──

  const flattenToc = (items: WikiTOC[], depth: number = 0): { id: string; description: string; depth: number }[] => {
    let result: { id: string; description: string; depth: number }[] = [];
    for (const item of items) {
      result.push({ id: item.id, description: item.description, depth });
      if (item.children) {
        result = result.concat(flattenToc(item.children, depth + 1));
      }
    }
    return result;
  };
  const allTocItems = flattenToc(tocItems);

  // ── Loading state ──

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 flex justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 fade-in">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-500" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">
            {isEditing ? 'Edit Article' : 'New Article'}
          </h1>
          <p className="text-sm text-slate-500">
              Use the rich Markdown editor below. Content is saved as Markdown.
          </p>
        </div>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          ✓ Saved successfully! Redirecting...
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Article title..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-semibold"
            required
          />
        </div>

        {/* Section */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Section <span className="text-red-500">*</span>
          </label>
          <select
            value={tocId}
            onChange={(e) => setTocId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            required
          >
            <option value="">Select a section...</option>
            {allTocItems.map(item => (
              <option key={item.id} value={item.id}>
                {'—'.repeat(item.depth * 2)} {item.description}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., getting-started, api, configuration"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
          />
        </div>

        {/* ── MDX Editor ── */}
        <MDXEditor
            ref={mdxEditorRef}
            markdown={markdown}
            onChange={(md) => setMarkdown(md)}
            contentEditableClassName="prose prose-slate max-w-none focus:outline-none min-h-[400px] py-3 px-4"
            className="mdx-editor-wrapper"
            // Plugins that power the editor
            plugins={[
                headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
                listsPlugin(),
                quotePlugin(),
                markdownShortcutPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                imagePlugin({
                  imageUploadHandler: async (imageUpload) => {
                  // Upload the file to Azure Blob Storage
                  const result = await imageApi.upload(imageUpload);
                  // If there is an error during upload, return an error message
                  if (!result || 'error' in result) {
                      const errorMsg = result && 'error' in result ? result.error : 'Failed to upload image.';
                      toast.error('Image upload failed: ' + errorMsg);
                      return "ERROR";
                  }
                  // Store the relative /api path so article content stays portable
                  // across environments; it is rebased for display below.
                  return result.url
                },
                  // The Functions app is on another origin, so relative /api
                  // paths have to be rebased before the editor can render them.
                  imagePreviewHandler: async (src) => resolveApiUrl(src),
                }),
                codeBlockPlugin({ defaultCodeBlockLanguage: 'js' }),
                codeMirrorPlugin({ codeBlockLanguages: { js: 'JavaScript', css: 'CSS', tsx: 'TypeScript (React)', json: 'JSON' } }),
                tablePlugin(),
                thematicBreakPlugin(),
                diffSourcePlugin( {viewMode: 'rich-text', diffMarkdown: originalMarkdown, readOnlyDiff: true }),
                toolbarPlugin({
                  toolbarClassName: 'my-classname',
                  toolbarContents: () => (
                    <>
                    <DiffSourceToggleWrapper>
                      <UndoRedo />
                      <BlockTypeSelect />
                      <BoldItalicUnderlineToggles />
                      <StrikeThroughSupSubToggles />
                      <HighlightToggle />
                      <InsertThematicBreak />
                      <CreateLink />
                      <InsertImage />
                      <InsertTable />
                      <ListsToggle />
                      <ConditionalContents
                        options={[
                          { when: (editor) => editor?.editorType === 'codeblock', contents: () => <ChangeCodeMirrorLanguage /> },
                          {
                            fallback: () => (
                              <>
                                <InsertCodeBlock />
                              </>
                            )
                          }
                        ]}
                      />
                    </DiffSourceToggleWrapper>
                    </>
                  )
                })
            ]}
            placeholder="Write your article content in Markdown..."
            spellCheck
            autoFocus={false}
        />

        {/* Publish toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isPublished"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="isPublished" className="text-sm text-slate-700">
            Publish immediately
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default WikiEditorPage;